// frontend/src/components/UpgradePromptModal.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  useTheme
} from '@mui/material';
import {
  Warning as WarningIcon,
  Bolt as BoltIcon
} from '@mui/icons-material';
import { useExtensionSubscription } from '../hooks/useExtensionSubscription';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEYS = {
  SESSION_DISMISSED: 'upgrade_prompt_dismissed',
  DAILY_DISMISSED: 'upgrade_prompt_dismissed_today'
};

/**
 * Global upgrade prompt modal that shows when:
 * - User is trial_expired tier
 * - Sync was blocked recently (within 7 days)
 * - User hasn't dismissed it this session
 * - User hasn't checked "Don't show today"
 */
export default function UpgradePromptModal() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { status, isPro, isTrialExpired, hasSyncBlockedRecently, currentPrice, hasDiscount, loading } = useExtensionSubscription();

  const [open, setOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);

  // Check if we should show the modal
  useEffect(() => {
    if (loading || !currentUser || !status) return;

    // Don't show for pro users
    if (isPro()) return;

    // Only show for trial_expired users with recent blocked sync
    if (!isTrialExpired() || !hasSyncBlockedRecently()) return;

    // Check session dismissal
    if (sessionStorage.getItem(STORAGE_KEYS.SESSION_DISMISSED)) return;

    // Check daily dismissal (24-hour expiry)
    const dailyDismissed = localStorage.getItem(STORAGE_KEYS.DAILY_DISMISSED);
    if (dailyDismissed) {
      const dismissedAt = parseInt(dailyDismissed, 10);
      const hoursSince = (Date.now() - dismissedAt) / (1000 * 60 * 60);
      if (hoursSince < 24) return;
      // Clear expired flag
      localStorage.removeItem(STORAGE_KEYS.DAILY_DISMISSED);
    }

    // Show the modal
    setOpen(true);
  }, [loading, currentUser, status, isPro, isTrialExpired, hasSyncBlockedRecently]);

  const handleUpgrade = () => {
    setOpen(false);
    navigate('/upgrade');
  };

  const handleMaybeLater = () => {
    // Dismiss for this session only
    sessionStorage.setItem(STORAGE_KEYS.SESSION_DISMISSED, 'true');

    // If checkbox is checked, also dismiss for 24 hours
    if (dontShowToday) {
      localStorage.setItem(STORAGE_KEYS.DAILY_DISMISSED, Date.now().toString());
    }

    setOpen(false);
  };

  // Format the blocked date for display
  const getBlockedDateDisplay = () => {
    if (!status?.lastSyncBlockedAt) return 'recently';
    const blockedAt = new Date(status.lastSyncBlockedAt);
    const now = new Date();
    const diffMs = now - blockedAt;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays === 1) return 'yesterday';
    return `${diffDays} days ago`;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleMaybeLater}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      {/* Warning header banner */}
      <Box sx={{
        bgcolor: theme.palette.warning.main,
        py: 2,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <WarningIcon sx={{ color: 'white', fontSize: 28 }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Your session sync was blocked
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We detected you tried to sync from the browser extension <strong>{getBlockedDateDisplay()}</strong>,
          but your free trial has ended.
        </Typography>

        {/* Usage stats box */}
        <Box sx={{
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          borderRadius: 2,
          p: 2,
          mb: 3
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Free tier: 1 sync/month
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your usage: <strong>{status?.monthlySessionCount ?? 1}/1</strong> syncs used
          </Typography>
        </Box>

        {/* Upgrade pitch */}
        <Alert
          severity="info"
          icon={<BoltIcon />}
          sx={{
            mb: 2,
            '& .MuiAlert-icon': { color: theme.palette.primary.main }
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            Upgrade to Pro for unlimited sync
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {hasDiscount() ? (
              <>
                <Box component="span" sx={{ textDecoration: 'line-through', mr: 1 }}>
                  $1.99/month
                </Box>
                <Box component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                  ${currentPrice()}/month (early adopter price)
                </Box>
              </>
            ) : (
              <Box component="span" fontWeight={600}>
                ${currentPrice()}/month
              </Box>
            )}
          </Typography>
        </Alert>

        {/* Features list */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pro includes:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Unlimited session sync across all devices
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Bulk export to JSON/CSV
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Voice recording & transcription
            </Typography>
          </Box>
        </Box>

        {/* Don't show today checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowToday}
              onChange={(e) => setDontShowToday(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              Don't show this again today
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleUpgrade}
          sx={{
            flex: 1,
            borderRadius: 3,
            py: 1.5,
            fontWeight: 600
          }}
        >
          Upgrade Now
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={handleMaybeLater}
          sx={{
            flex: 1,
            borderRadius: 3,
            py: 1.5
          }}
        >
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
}
